import { useId, type FormEvent } from "react";
import type { Lut, LutInput } from "@/api/settings";
import { RequestError } from "@/components/RequestError";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

const FY_CODE = /^(\d{2})-(\d{2})$/;

type LutFormProps = {
  initial?: Lut | null;
  submitLabel?: string;
  onSubmit: (input: LutInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function LutForm({ initial, submitLabel = "Save LUT", onSubmit, onCancel, onReload }: LutFormProps) {
  const id = useId();
  const { values, set, setValues } = useFormValues<LutInput>({
    fy_code: initial?.fy_code ?? "",
    arn: initial?.arn ?? "",
    filed_on: initial?.filed_on ?? "",
    valid_from: initial?.valid_from ?? "",
    valid_to: initial?.valid_to ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();

  function changeFy(value: string): void {
    const match = FY_CODE.exec(value.trim());
    setValues((current) => {
      const next = { ...current, fy_code: value };
      if (match && !current.valid_from && !current.valid_to) {
        const start = 2000 + Number(match[1]);
        next.valid_from = `${start}-04-01`;
        next.valid_to = `${start + 1}-03-31`;
      }
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit({ ...values, fy_code: values.fy_code.trim(), arn: values.arn.trim() }));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-fy`}
          label="Financial year"
          value={values.fy_code}
          onChange={changeFy}
          error={errors.fy_code ?? errors._form}
          hint="e.g. 26-27 for April 2026 to March 2027"
          maxLength={5}
        />
        <TextField
          id={`${id}-arn`}
          label="ARN"
          value={values.arn}
          onChange={(value) => set("arn", value)}
          error={errors.arn}
          hint="Acknowledgement reference number from the GST portal"
          autoCapitalize="characters"
        />
        <TextField
          id={`${id}-filed`}
          label="Filed on"
          type="date"
          value={values.filed_on}
          onChange={(value) => set("filed_on", value)}
          error={errors.filed_on}
        />
        <div className="hidden md:block" />
        <TextField
          id={`${id}-from`}
          label="Valid from"
          type="date"
          value={values.valid_from}
          onChange={(value) => set("valid_from", value)}
          error={errors.valid_from}
        />
        <TextField
          id={`${id}-to`}
          label="Valid to"
          type="date"
          value={values.valid_to}
          onChange={(value) => set("valid_to", value)}
          error={errors.valid_to}
        />
      </div>
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
